import { useEffect, useEffectEvent, useMemo, useState } from 'react';
import { useStableQuery } from '../../db/useStableQuery';
import { FormattedMessage, useIntl } from 'react-intl';
import { useForm, useWatch, type Resolver } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import Autocomplete from '@mui/material/Autocomplete';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import TextField from '@mui/material/TextField';
import { mdiInformationVariantBox, mdiLandslideOutline } from '@mdi/js';
import LayersIcon from '@mui/icons-material/Layers';
import { v4 as uuid } from 'uuid';
import { useRxCollection, useLiveRxQuery } from 'rxdb/plugins/react';
import {
    areaDescriptionInputSchema,
    type AreaDescriptionDocType,
    type ParcelDocType
} from '@ast-data/shared';
import { useValidationMessage } from '../../i18n/validationMessages';
import { useAbility, scopedSubject } from '../auth/abilityContext';
import { useRecordTranslations } from '../translation/recordTranslations';
import { useSavedToast } from '../../components/toastContext';
import { MediaSectionBox } from './descriptionSections';
import { useFamilyProjectIds } from '../projects/useFamilyProjectIds';
import { useFamilyDescriptions } from './useFamilyDescriptions';
import { useUserLabel } from '../users/useUserLabel';
import { useActiveUsers } from '../users/useActiveUsers';
import { usePersonPicker } from '../../components/personPicker';
import { useEmploymentDay } from '../users/employment';
import { useCurrentPerson, usePersonUserId } from '../users/personUserId';
import { useSelfPrefill } from '../users/useSelfPrefill';
import RecordedAtField from '../../components/RecordedAtField';
import MdiIcon from '../../components/MdiIcon';
import {
    bindFormFields,
    labelIdsFrom,
    requiredInForm
} from '../../components/formFields';
import { renderCheckableOption } from '../../components/checkableOption';
import { chipClipboardProps } from '../../components/chipClipboard';
import FormRow from '../../components/FormRow';
import FormDialogAppBar from '../../components/FormDialogAppBar';
import RecordFormDialog from '../../components/RecordFormDialog';
import FormSectionBox from '../../components/FormSectionBox';
import { SectionNavMenuButton } from '../../components/formSectionNav';
import { type FormSection as FormSectionNavEntry } from '../../components/formSectionNavContext';
import { useGuardedNavigate } from '../../components/useGuardedNavigate';
import { useRecordSiblings } from '../../components/useRecordSiblings';
import { recordSortModel } from '../../components/useRecordGrid';
import { AREA_DESCRIPTION_GRID } from './recordGrids';
import { useFindingRefSupport } from './useFindingRefSupport';
import { useAreaDescriptionPdfExport } from '../export/areaDescriptionPdf/useAreaDescriptionPdfExport';
import {
    useForwardBackState,
    useDialogBackCrumb
} from '../../components/dialogBackTrail';
import { useUnsavedCloseGuard } from '../../components/useUnsavedCloseGuard';
import { useFocusFieldOnOpen } from '../../components/cellFocus';
import { parcelPickerLabel } from './parcelPickerLabel';
import {
    areaDescriptionKey,
    areaDescriptionLocation
} from './areaDescriptionLabel';
import { compareNatural } from '../../utils/compare';

type FormValues = {
    area: string;
    planum: string;
    sondage: string;
    length: string;
    width: string;
    conditionAtRecording: string;
    naturalSoil: string;
    findingsOverview: string;
    editor: string;
    recordedAt: string;
};

function emptyValues(): FormValues {
    return {
        area: '',
        planum: '',
        sondage: '',
        length: '',
        width: '',
        conditionAtRecording: '',
        naturalSoil: '',
        findingsOverview: '',
        editor: '',
        recordedAt: new Date().toISOString()
    };
}

function toValues(description: AreaDescriptionDocType): FormValues {
    return {
        area: description.area,
        planum: description.planum,
        sondage: description.sondage,
        length: description.length,
        width: description.width,
        conditionAtRecording: description.conditionAtRecording,
        naturalSoil: description.naturalSoil,
        findingsOverview: description.findingsOverview,
        editor: description.editor,
        recordedAt: description.recordedAt
    };
}

function distinct(values: string[]): string[] {
    return [
        ...new Set(values.map((value) => value.trim()).filter(Boolean))
    ].sort((firstValue, secondValue) =>
        compareNatural(firstValue, secondValue)
    );
}

const FORM_SECTIONS: FormSectionNavEntry[] = [
    { id: 'identity', titleId: 'areaDescription.section.identity' },
    { id: 'extent', titleId: 'areaDescription.section.extent' },
    { id: 'description', titleId: 'areaDescription.section.description' },
    { id: 'media', titleId: 'media.title' }
];

interface AreaDescriptionFormDialogProps {
    open: boolean;
    onClose: () => void;
    projectId: string;
    areaDescription?: AreaDescriptionDocType | null;
    /** Grid column the user double-clicked, to focus the matching input on open. */
    focusField?: string;
}

/**
 * The person the record is written up by is required in the form — see
 * requiredInForm.
 */
const AREA_DESCRIPTION_FORM_SCHEMA = requiredInForm(
    areaDescriptionInputSchema,
    ['editor']
);

export default function AreaDescriptionFormDialog({
    open,
    onClose,
    projectId,
    areaDescription,
    focusField
}: AreaDescriptionFormDialogProps) {
    const intl = useIntl();
    const collection =
        useRxCollection<AreaDescriptionDocType>('area_descriptions');
    const validationMessage = useValidationMessage();
    const notifySaved = useSavedToast();
    const isEdit = !!areaDescription;

    // Written by the Teamleitung and the project office only; everyone else
    // opens it to read.
    const ability = useAbility();
    const canEditMedia = ability.can(
        'update',
        scopedSubject('AreaDescription', { projectId })
    );
    const readOnly = !ability.can(
        isEdit ? 'update' : 'create',
        scopedSubject('AreaDescription', { projectId })
    );

    // Fläche × Planum is unique family-wide: the Befunde — and with them the
    // Flächen — are shared across a family's Teilmaßnahmen.
    const familyIds = useFamilyProjectIds(projectId);
    const familyQuery = useStableQuery<AreaDescriptionDocType>({
        selector: { projectId: { $in: familyIds } }
    });
    const { results: familyDocs } = useLiveRxQuery<AreaDescriptionDocType>({
        collection: 'area_descriptions',
        query: familyQuery
    });
    const familyAreaDescriptions = useMemo(
        () =>
            familyDocs.map(
                (doc) => doc.toMutableJSON() as AreaDescriptionDocType
            ),
        [familyDocs]
    );

    // The Befund descriptions already name the Flächen and Plana dug so far —
    // the natural suggestions, though the fields stay free text.
    const { planums, profiles } = useFamilyDescriptions(projectId);

    const parcelsQuery = useStableQuery<ParcelDocType>({
        selector: { projectId }
    });
    const { results: parcelsDocs } = useLiveRxQuery<ParcelDocType>({
        collection: 'parcels',
        query: parcelsQuery
    });
    const sortedParcels = useMemo(
        () =>
            parcelsDocs
                .map((doc) => doc.toMutableJSON() as ParcelDocType)
                .sort((firstParcel, secondParcel) =>
                    compareNatural(
                        parcelPickerLabel(firstParcel),
                        parcelPickerLabel(secondParcel)
                    )
                ),
        [parcelsDocs]
    );
    const linkedParcelIds = useMemo(
        () => areaDescription?.parcelIds ?? [],
        [areaDescription]
    );
    const [selectedParcelIds, setSelectedParcelIds] = useState<string[]>([]);
    const selectedParcels = useMemo(
        () =>
            sortedParcels.filter((parcel) =>
                selectedParcelIds.includes(parcel.id)
            ),
        [sortedParcels, selectedParcelIds]
    );

    const userName = useUserLabel();
    const users = useActiveUsers();
    const currentUserName = useCurrentPerson()?.name ?? '';
    // A name nobody touched keeps its account even where the rule can no
    // longer place it (see usePersonUserId).
    const keptLinks = useMemo(
        () =>
            areaDescription
                ? [
                      {
                          name: areaDescription.editor,
                          id: areaDescription.editorId
                      }
                  ]
                : [],
        [areaDescription]
    );
    const { resolve } = usePersonUserId(keptLinks);

    const suggestions = useMemo(
        () => ({
            editor: distinct([
                ...users.map(userName),
                ...familyAreaDescriptions.map(
                    (description) => description.editor
                )
            ]),
            area: distinct([
                ...planums.map((planum) => planum.area),
                ...profiles.map((profile) => profile.area),
                ...familyAreaDescriptions.map((description) => description.area)
            ]),
            planum: distinct([
                ...planums.map((planum) => planum.planumNumber),
                ...familyAreaDescriptions.map(
                    (description) => description.planum
                )
            ]),
            sondage: distinct([
                ...planums.map((planum) => planum.sondage),
                ...profiles.map((profile) => profile.sondage),
                ...familyAreaDescriptions.map(
                    (description) => description.sondage
                )
            ])
        }),
        [familyAreaDescriptions, planums, profiles, users, userName]
    );

    const {
        register,
        handleSubmit,
        reset,
        getValues,
        setValue,
        control,
        setError,
        setFocus,
        formState: { errors, isSubmitting, isDirty }
    } = useForm<FormValues>({
        resolver: zodResolver(
            AREA_DESCRIPTION_FORM_SCHEMA
        ) as unknown as Resolver<FormValues>,
        defaultValues: emptyValues()
    });

    // Only who was employed on the Aufnahmedatum (see users/employment).
    const employmentDay = useEmploymentDay(control, 'recordedAt');
    const personPicker = usePersonPicker(employmentDay);

    // What a downloaded picture is named after — read live, so a Fläche just
    // typed is already in the file name.
    const [watchedArea, watchedPlanum] = useWatch({
        control,
        name: ['area', 'planum']
    });

    const { text, suggest, person, richText } = bindFormFields<FormValues>({
        intl,
        register,
        control,
        errors,
        validationMessage,
        labelId: labelIdsFrom('areaDescription.field'),
        readOnly,
        unitId: (name) =>
            name === 'length' || name === 'width' ? 'common.metres' : undefined
    });

    // The account names can arrive after the dialog opened (see useSelfPrefill).
    useSelfPrefill(open && !areaDescription, currentUserName, (self) => {
        if (getValues('editor').trim() === '') {
            setValue('editor', self);
        }
    });

    // An effect EVENT, so the reset runs only when the dialog (re)opens for a
    // record — not on every background sync.
    const resetForOpening = useEffectEvent(() => {
        const values = areaDescription
            ? toValues(areaDescription)
            : emptyValues();
        reset({
            ...values,
            editor: values.editor || currentUserName
        });
        setSelectedParcelIds(linkedParcelIds);
    });
    useEffect(() => {
        if (open) {
            // react-hook-form's `reset` publishes to its own subscribers, so it
            // cannot be moved into the render pass the way plain state can.
            // eslint-disable-next-line react-hooks/set-state-in-effect
            resetForOpening();
        }
    }, [open, areaDescription]);

    useFocusFieldOnOpen(setFocus, open, focusField);

    // The Flurstücke are local state — fold them into the dirty check.
    const parcelsDirty = useMemo(
        () =>
            [...selectedParcelIds].sort().join(',') !==
            [...linkedParcelIds].sort().join(','),
        [selectedParcelIds, linkedParcelIds]
    );

    const translations = useRecordTranslations({
        ownerType: 'areaDescription',
        ownerId: areaDescription?.id,
        projectId,
        readOnly
    });
    const closeGuard = useUnsavedCloseGuard({
        open,
        isDirty: isDirty || parcelsDirty || translations.isDirty,
        onClose,
        onSave: () => handleSubmit(onSubmit)()
    });
    const selfLabel = intl.formatMessage(
        { id: 'areaDescription.label' },
        {
            location: areaDescription
                ? areaDescriptionLocation(areaDescription)
                : '?'
        }
    );
    const guardedNav = useGuardedNavigate({
        isDirty: isDirty || parcelsDirty || translations.isDirty,
        submit: () => handleSubmit(onSubmit)(),
        close: () => closeGuard.close(),
        forwardState: useForwardBackState(selfLabel)
    });
    const backCrumb = useDialogBackCrumb(closeGuard.requestBack);
    const siblings = useRecordSiblings<AreaDescriptionDocType>({
        collection: AREA_DESCRIPTION_GRID.entity.collection,
        projectId,
        currentId: areaDescription?.id ?? '',
        sortModel: recordSortModel(AREA_DESCRIPTION_GRID),
        label: (row) => areaDescriptionLocation(row),
        requestNavigate: guardedNav.requestNavigate
    });

    // Defined AFTER `guardedNav`: a ringed "Bef. 7000" is a link out of an open
    // editor, so it has to ask before leaving.
    const findingRefs = useFindingRefSupport(
        projectId,
        guardedNav.requestNavigate
    );

    async function onSubmit(data: FormValues) {
        if (!collection || readOnly) {
            return;
        }
        const key = areaDescriptionKey(data.area, data.planum, data.sondage);
        const taken = familyAreaDescriptions.some(
            (existing) =>
                existing.id !== (areaDescription?.id ?? '') &&
                areaDescriptionKey(
                    existing.area,
                    existing.planum,
                    existing.sondage
                ) === key
        );
        if (taken) {
            // A Sondage is what tells two descriptions of one Planum apart, so
            // it is where a clash is fixed.
            setError(
                data.sondage.trim() ? 'sondage' : 'area',
                {
                    type: 'validate',
                    message: data.sondage.trim()
                        ? 'duplicateAreaPlanumSondage'
                        : 'duplicateAreaPlanum'
                },
                { shouldFocus: true }
            );
            return;
        }

        const now = new Date().toISOString();
        const embedded = {
            parcelIds: selectedParcelIds,
            editorId: resolve(data.editor)
        };
        const savedId = isEdit ? areaDescription!.id : uuid();
        if (isEdit) {
            const doc = await collection.findOne(savedId).exec();
            if (doc) {
                await doc.incrementalPatch({
                    ...data,
                    ...embedded,
                    updatedAt: now
                });
            }
        } else {
            await collection.insert({
                id: savedId,
                projectId,
                ...data,
                ...embedded,
                createdBy: '',
                updatedBy: '',
                updatedAt: now,
                createdAt: now
            });
        }
        await translations.commit(savedId);
        notifySaved();
        // A pending "save and go" reference target wins over closing.
        guardedNav.finishSubmit();
    }

    // Parked away from Speichern/Abbrechen — not one of the dialog's own
    // actions. Only for a SAVED record: the export reads the stored one. A
    // reader may print too — printing is reading.
    const { exportPdf, busy: exportingPdf } = useAreaDescriptionPdfExport();
    const pdfButton = isEdit && areaDescription && (
        <Button
            variant="outlined"
            color="inherit"
            startIcon={
                exportingPdf ? (
                    <CircularProgress size={18} color="inherit" />
                ) : (
                    <PictureAsPdfIcon />
                )
            }
            disabled={exportingPdf}
            onClick={() => void exportPdf([areaDescription.id])}
            sx={{ mr: 'auto' }}
        >
            <FormattedMessage id="areaDescriptionPdf.export.one" />
        </Button>
    );

    return (
        <RecordFormDialog
            open={open}
            sections={FORM_SECTIONS}
            onSubmit={handleSubmit(onSubmit)}
            closeGuard={closeGuard}
            navGuard={guardedNav}
            isSubmitting={isSubmitting}
            readOnly={readOnly}
            translations={translations}
            footerStartSlot={pdfButton}
            appBar={
                <FormDialogAppBar
                    projectId={projectId}
                    onClose={closeGuard.requestClose}
                    startSlot={<SectionNavMenuButton />}
                    backCrumb={backCrumb}
                    siblings={siblings}
                    title={
                        isEdit && areaDescription ? (
                            <FormattedMessage
                                id="areaDescription.label"
                                values={{
                                    location:
                                        areaDescriptionLocation(areaDescription)
                                }}
                            />
                        ) : (
                            <FormattedMessage id="areaDescription.form.newTitle" />
                        )
                    }
                />
            }
        >
            <FormSectionBox titlePrefix="areaDescription.section" id="identity">
                <FormRow>
                    {suggest('area', suggestions.area)}
                    {suggest('planum', suggestions.planum)}
                    {suggest('sondage', suggestions.sondage)}
                </FormRow>
                <FormRow>
                    <RecordedAtField
                        control={control}
                        name="recordedAt"
                        readOnly={readOnly}
                    />
                    {person('editor', personPicker)}
                </FormRow>
            </FormSectionBox>

            <FormSectionBox titlePrefix="areaDescription.section" id="extent">
                <Autocomplete
                    multiple
                    disableCloseOnSelect
                    readOnly={readOnly}
                    renderOption={renderCheckableOption({
                        ticks: true,
                        label: parcelPickerLabel
                    })}
                    options={sortedParcels}
                    value={selectedParcels}
                    onChange={(_event, value) =>
                        setSelectedParcelIds(value.map((parcel) => parcel.id))
                    }
                    {...chipClipboardProps({
                        value: selectedParcels,
                        getLabel: parcelPickerLabel,
                        options: sortedParcels,
                        onChange: (value) =>
                            setSelectedParcelIds(
                                value.map((parcel) => parcel.id)
                            ),
                        readOnly
                    })}
                    getOptionLabel={parcelPickerLabel}
                    isOptionEqualToValue={(option, value) =>
                        option.id === value.id
                    }
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label={intl.formatMessage({
                                id: 'areaDescription.field.parcels'
                            })}
                            placeholder={intl.formatMessage({
                                id: 'areaDescription.field.parcelsPlaceholder'
                            })}
                        />
                    )}
                />
                <FormRow>
                    {text('length')}
                    {text('width')}
                </FormRow>
            </FormSectionBox>

            <FormSectionBox
                titlePrefix="areaDescription.section"
                id="description"
            >
                {richText('conditionAtRecording', {
                    icon: <MdiIcon path={mdiInformationVariantBox} />,
                    minRows: 5,
                    findingRefs
                })}
                {richText('naturalSoil', {
                    icon: <MdiIcon path={mdiLandslideOutline} />,
                    minRows: 5,
                    findingRefs
                })}
                {/* The overall impression, not a list: a "Bef. 7000" named here
                    is ringed and links to the Befund, and that is all. */}
                {richText('findingsOverview', {
                    icon: <LayersIcon />,
                    minRows: 5,
                    findingRefs
                })}
            </FormSectionBox>

            <MediaSectionBox
                projectId={projectId}
                ownerType="areaDescription"
                ownerId={isEdit && areaDescription ? areaDescription.id : null}
                canEdit={canEditMedia}
                location={{ area: watchedArea, planumNumber: watchedPlanum }}
                onNavigate={guardedNav.requestNavigate}
            />
        </RecordFormDialog>
    );
}
